/**
 * AuthContext — Google SSO via @react-oauth/google.
 *
 * Flow:
 *   1. User clicks "Continue with Google" → useGoogleLogin opens popup
 *   2. Google returns an access_token in onSuccess
 *   3. We POST { accessToken, rememberMe } to the worker's /api/auth/google
 *   4. Worker verifies the token via Google's userinfo endpoint, applies
 *      domain restriction (@parkify.com), issues a session JWT
 *   5. We store the JWT + profile in localStorage (8h or 7d TTL)
 *   6. authHeaders() returns Authorization: Bearer <jwt> for outgoing requests
 *
 * Microsoft is intentionally STUBBED for now — when client-facing sign-in
 * lands later, swap signInMicrosoft from console.warn to MSAL.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { useGoogleLogin, type TokenResponse } from '@react-oauth/google'
import { hasPermission, getPermissionsForRole, type Permission, type Role } from './permissions'

// Per-tenant feature flags now come from the one registry in
// @parkify/api-types (packages/api-types/src/features.ts) — the same
// declaration the worker stores against and Parkify Admin renders its
// checkboxes from. Re-exported here because this module was the de-facto
// home for the type and plenty of screens import it from us.
import {
  isFeatureEnabledFor, type ClientFeatures, type UserFeatures,
} from '@parkify/api-types'
import { trackUsage } from '../utils/usage'
import { clearAllDrafts } from '../screens/support/draftStore'
import {
  buildViewAsTarget, type PermissionMatrix, type ViewAsClientRecord, type ViewAsUserRecord,
} from './viewAsTarget'
import { isViewAsBlockedRequest, VIEW_AS_BLOCKED_MESSAGE } from './viewAsReadOnly'
import { API_BASE, callApi } from '../config/apiBase'
export type { ClientFeatures, UserFeatures }

// Caller's tenant — branding + flags applied to the dashboard UI. Returned
// by /api/admin/users/me on every refresh so updates to the tenant record
// propagate without a re-login.
export interface ClientContext {
  id: string
  name: string
  logoUrl?: string
  iconUrl?: string
  accentColor?: string
  features: ClientFeatures
}

interface AuthUser {
  email: string
  name?: string
  picture?: string
  provider: 'google' | 'microsoft' | 'email'
  role: Role
  // Tenant scope. null = Parkify staff (global). Non-null = client-scoped.
  clientId: string | null
  permissions?: Permission[]    // Effective permissions, fetched from /me
  client?: ClientContext | null  // Tenant branding + flags, fetched from /me
  /**
   * THIS user's own flag narrowing, fetched from /me — kept separate from
   * `client.features` rather than pre-merged, because the two answer different
   * questions ("does the tenant have this?" vs "did this person hide it?") and
   * the admin screens need to tell them apart.
   */
  userFeatures?: UserFeatures | null
  /**
   * /me's `scope` exactly as received: the canonical scope the Worker
   * authorizes this session's location and zone writes against
   * (packages/api-types caller-scope.ts). Deliberately `unknown`: it is also
   * persisted to localStorage, so every reader validates it with
   * parseCallerScope at the point of use and fails closed on anything off
   * contract (absent before the first /me, an older Worker, a malformed or
   * edited value). That parse lives with its readers rather than here so it
   * stays out of the console's budgeted initial payload. Never infer scope
   * from the zones a screen loaded. Forced to null under View as (see
   * effectiveUser).
   */
  scope?: unknown
  /**
   * Every client this person belongs to, current first, from /me (api
   * auth/switch_client.ts). Empty unless they are in two or more; drives the
   * sidebar's client switcher. Names only: switching mints a new session.
   */
  memberships?: ClientMembershipOption[]
  /** True when the API is serving this signed-in person one consolidated portfolio. */
  portfolio?: boolean
}

export interface ClientMembershipOption {
  clientId: string
  name: string
}

// A user an owner/admin is "viewing as" — see-through-their-lens (read-only).
// Just enough to recompute the access lens: role drives permissions (derived
// locally via getPermissionsForRole), clientId drives the staff-vs-tenant gate,
// and the optional client carries the target tenant's feature flags + branding
// so hidden sidebar sections match exactly what that user would see.
//
// A SNAPSHOT of the target's record, so AuthProvider re-reads it live whenever
// a lens opens or reloads (see the refresh effect). The worker swaps scope from
// the live record on every request; a stale copy here would label and gate the
// console as someone the target no longer is.
export interface ViewAsTarget {
  email: string
  name?: string
  role: Role
  clientId: string | null
  client?: ClientContext | null
  // The target's own per-user nav narrowing (AdminUser.features).
  userFeatures?: UserFeatures | null
  // The target's permissions from the worker's live matrix (Parkify Admin →
  // Roles). Absent on a copy saved before this field existed: falls back to
  // the built-in defaults for their role.
  permissions?: Permission[]
}

interface AuthContextValue {
  // Effective identity — equals realUser normally, or the impersonated
  // target's lens while viewing-as. Screens read THIS for gating + display.
  user: AuthUser | null
  loading: boolean
  error: string | null
  signInGoogle: (rememberMe?: boolean) => void
  signInMicrosoft: (rememberMe?: boolean) => void
  // Whether a real Google OAuth client id is configured. False ⇒ the sign-in
  // card hides the Google button and offers only the email magic link (the
  // console still boots — main.tsx mounts GoogleOAuthProvider with a placeholder).
  googleConfigured: boolean
  // Provider-agnostic email magic link. `requestEmailLink` emails a one-time
  // sign-in link (always resolves true on a well-formed request — the server is
  // enumeration-safe and reveals nothing about whether the address has an
  // account). `verifyEmailLink` trades the link's token for a session; the
  // /verify route calls it on mount.
  requestEmailLink: (email: string, rememberMe?: boolean) => Promise<boolean>
  verifyEmailLink: (linkToken: string) => Promise<void>
  signOut: () => void
  authHeaders: () => HeadersInit
  // Same token, but WITHOUT the view-as header — so the call runs as the real
  // operator. Needed by the picker itself: now that the server applies the
  // target's scope to every lensed request, a lensed /api/admin/users would 403
  // (the target isn't an admin) and the picker couldn't render its own exit UI.
  realAuthHeaders: () => HeadersInit
  hasPerm: (perm: Permission) => boolean
  // Convenience: feature flag check. Returns true if the caller's tenant
  // has the feature enabled (or if no flag is set — default enabled).
  hasFeature: (key: keyof ClientFeatures) => boolean
  // ── View-as (read-only impersonation) ────────────────────────────────────
  // The real signed-in user, unaffected by view-as. The banner + audit read
  // this; authHeaders() always carries realUser's token.
  realUser: AuthUser | null
  // Non-null while viewing-as. `user` already reflects the target's lens.
  viewingAs: ViewAsTarget | null
  // True only for Parkify staff (clientId null) with view.parkify_admin —
  // owner/admin. Gates the picker. Based on realUser, never the target.
  canImpersonate: boolean
  startViewingAs: (target: ViewAsTarget) => void
  stopViewingAs: () => void
  // ── Sign in as a client (REAL client-scoped session, not the lens) ───────
  // Non-null while the active session is a staff-minted client `manager`
  // session (POST /api/clients/:id/sign-in-as). Unlike view-as this swaps the
  // TOKEN — the server treats every request as a real client user, so what you
  // see is exactly what the client gets: their flags, no staff carve-outs.
  clientSession: ClientSessionInfo | null
  signInAsClient: (client: { id: string; name: string }) => Promise<boolean>
  exitClientSession: () => void
  // ── Switch client (a person in several clients) ───────────────────────────
  // Re-issues THIS person's own session for another client they belong to
  // (POST /api/admin/users/me/switch-client). Not a lens: the new token is
  // their real session, and the console reloads so no screen keeps data from
  // the client it left.
  switchClient: (clientId: string) => Promise<boolean>
}

export interface ClientSessionInfo {
  clientId: string
  clientName: string
}

const AuthContext = createContext<AuthContextValue | null>(null)

const STORAGE_KEY = 'parkify_auth_v1'
// The view-as target, kept in sessionStorage so the lens survives a reload of
// THIS tab and nothing else. It used to live in localStorage, which made it a
// property of the browser: a lens opened on Monday was still on when a support
// link was opened in a new tab days later, so the console greeted the owner as
// the client user and bounced the staff-only Support screen to home, and the
// sidebar offered only "Sign out" (2026-09-25, Adrian viewing as a client user).
// A new tab now always opens as yourself. Cleared on sign-out and whenever the
// real user isn't eligible to impersonate.
export const VIEW_AS_KEY = 'parkify_view_as_user_v1'
// A real Google client id present ⇒ the Google SSO button is offered. Absent ⇒
// email magic-link only (main.tsx still mounts the provider with a placeholder).
const GOOGLE_CONFIGURED = !!(import.meta.env.VITE_AUTH_GOOGLE_CLIENT_ID as string | undefined)

interface StoredAuth {
  user: AuthUser
  token: string
  expiresAt: number  // epoch ms
  // Present ⇒ this session is a staff-minted "sign in as client" manager
  // session, held in CLIENT_SESSION_KEY; the staffer's own session is left
  // untouched in STORAGE_KEY until exit.
  clientSession?: ClientSessionInfo
}

// A "sign in as client" session lives in sessionStorage, scoped to the tab it
// was opened in, and is never written over the staffer's own session. Inside
// that tab it is THE session (refresh, expiry timer and 401 handler all read
// `stored`), which is the fidelity point; every other tab, and this one after
// it closes, is the staffer's own login. Before, the client token REPLACED
// STORAGE_KEY in localStorage and the real session was parked beside it, so the
// lens followed the operator into every new tab until they exited it.
const CLIENT_SESSION_KEY = 'parkify_client_session_v1'
// Where the pre-2026-09-25 build parked the staffer's session while the client
// token sat in STORAGE_KEY. Read once, on load, to put a browser left in that
// state back on the staffer's own session; never written.
const LEGACY_REAL_SESSION_KEY = 'parkify_real_auth_v1'

interface WorkerAuthResponse {
  token: string
  sub: string
  email: string
  name?: string
  picture?: string
  role?: Role
  clientId?: string | null
  error?: string
}

/** Shown whenever a session ended on its own rather than by the operator. */
export const SESSION_EXPIRED_MESSAGE = 'Your session expired. Please sign in again.'

/**
 * Result of reading the persisted session. `expired` is distinguished from
 * `none` on purpose: both leave the operator signed out, but only `expired`
 * should explain WHY. Collapsing them (the old `loadStored(): StoredAuth|null`)
 * meant reopening a tab after the token lapsed dropped you on a wordless
 * sign-in card.
 */
export type StoredRead =
  | { kind: 'none' }
  | { kind: 'expired' }
  | { kind: 'ok'; stored: StoredAuth }

function parseStored(raw: string | null): StoredRead {
  try {
    if (!raw) return { kind: 'none' }
    const parsed = JSON.parse(raw) as StoredAuth
    // A blob missing either field is corrupt, not expired — treat as no session
    // so we don't claim an expiry we can't actually establish.
    if (!parsed?.token || typeof parsed.expiresAt !== 'number') return { kind: 'none' }
    if (parsed.expiresAt < Date.now()) return { kind: 'expired' }
    return { kind: 'ok', stored: parsed }
  } catch {
    return { kind: 'none' }
  }
}

function safeGet(store: Storage, key: string): string | null {
  try { return store.getItem(key) } catch { return null }
}
function safeRemove(store: Storage, key: string): void {
  try { store.removeItem(key) } catch { /* non-fatal */ }
}

/** The staffer's own session in localStorage, never a client-lens one. */
function readOwnSession(): StoredRead {
  // A browser the old build left mid-lens holds the CLIENT token in STORAGE_KEY
  // and the real session parked beside it. Put the real one back (or drop the
  // lens token if the parked one is gone), so no load ever resumes a lens.
  const main = parseStored(safeGet(localStorage, STORAGE_KEY))
  const legacyParked = safeGet(localStorage, LEGACY_REAL_SESSION_KEY)
  if (main.kind === 'ok' && main.stored.clientSession) {
    const parked = parseStored(legacyParked)
    safeRemove(localStorage, LEGACY_REAL_SESSION_KEY)
    if (parked.kind === 'ok' && !parked.stored.clientSession) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(parked.stored)) } catch { /* non-fatal */ }
      return parked
    }
    safeRemove(localStorage, STORAGE_KEY)
    return { kind: 'none' }
  }
  if (legacyParked) safeRemove(localStorage, LEGACY_REAL_SESSION_KEY)
  return main
}

/** Exported for unit tests; AuthProvider is the only production caller. */
export function readStored(): StoredRead {
  // This tab's client session, when one is open and still valid, else your own.
  const lens = parseStored(safeGet(sessionStorage, CLIENT_SESSION_KEY))
  if (lens.kind === 'ok' && lens.stored.clientSession) {
    // A lens is only valid on top of a live staff login in this browser.
    if (readOwnSession().kind === 'ok') return lens
  }
  if (lens.kind !== 'none') safeRemove(sessionStorage, CLIENT_SESSION_KEY)
  return readOwnSession()
}

// NB: an expired blob is deliberately left in localStorage. Purging it here
// would make a SECOND reload show the wordless card again; leaving it means the
// explanation persists until the operator actually signs in (persist()
// overwrites it) or signs out (signOut removes it).

/**
 * Read the `exp` (seconds) out of a JWT's payload and return it as epoch ms.
 * Used by the email-link path, where the session TTL (8h vs 7d "remember me")
 * was decided server-side at request time and baked into the token — so the
 * token itself is the authoritative source for the local expiry. `exp` is a
 * public claim; no secret needed to read it.
 */
function jwtExpiryMs(token: string): number | null {
  try {
    const body = token.split('.')[1]
    if (!body) return null
    const parsed = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number }
    return typeof parsed.exp === 'number' ? parsed.exp * 1000 : null
  } catch {
    return null
  }
}

function persist(stored: StoredAuth): void {
  // A client-lens session never touches localStorage: see CLIENT_SESSION_KEY.
  try {
    if (stored.clientSession) sessionStorage.setItem(CLIENT_SESSION_KEY, JSON.stringify(stored))
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch { /* private mode / quota — non-fatal */ }
}

function saveViewAs(target: ViewAsTarget): void {
  try { sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(target)) } catch { /* non-fatal */ }
}
function clearViewAs(): void {
  safeRemove(sessionStorage, VIEW_AS_KEY)
  // The pre-2026-09-25 build kept the target in localStorage (see VIEW_AS_KEY).
  safeRemove(localStorage, VIEW_AS_KEY)
}

function loadViewAs(): ViewAsTarget | null {
  // A target the old build left in localStorage is dropped, not adopted: it is
  // exactly the browser-wide lens this key moved away from.
  safeRemove(localStorage, VIEW_AS_KEY)
  try {
    const raw = sessionStorage.getItem(VIEW_AS_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ViewAsTarget
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Read the persisted session exactly once, then seed both pieces of state
  // from it so an already-expired token explains itself on first paint.
  const [initialRead] = useState<StoredRead>(readStored)
  const [stored, setStored] = useState<StoredAuth | null>(
    initialRead.kind === 'ok' ? initialRead.stored : null,
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(
    initialRead.kind === 'expired' ? SESSION_EXPIRED_MESSAGE : null,
  )
  // Read-only impersonation target. Null = viewing as yourself.
  const [viewAs, setViewAs] = useState<ViewAsTarget | null>(() => loadViewAs())

  // useGoogleLogin's onSuccess is set at hook init time, so we capture the
  // user's "Remember me" choice via a ref read inside onSuccess at call time.
  const rememberRef = useRef(false)

  // Exchange a Google access_token for a worker-issued session JWT.
  const exchangeGoogleToken = useCallback(async (tokenResponse: TokenResponse): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await callApi(`/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken: tokenResponse.access_token,
          rememberMe: rememberRef.current,
        }),
      })
      const data = await res.json() as WorkerAuthResponse
      if (!res.ok) {
        throw new Error(data.error ?? `auth failed (${res.status})`)
      }
      const ttlMs = (rememberRef.current ? 7 * 24 : 8) * 3600 * 1000
      const newStored: StoredAuth = {
        user: {
          email: data.email,
          name: data.name,
          picture: data.picture,
          provider: 'google',
          role: data.role ?? 'viewer',  // least-privilege default — a missing/legacy role must never auto-grant owner (O9/D3)
          clientId: data.clientId ?? null,
        },
        token: data.token,
        expiresAt: Date.now() + ttlMs,
      }
      persist(newStored)
      setStored(newStored)
      trackUsage(API_BASE, { Authorization: `Bearer ${data.token}` }, 'login')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      console.error('[auth] Google sign-in failed:', msg)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  const googleLogin = useGoogleLogin({
    flow: 'implicit',  // returns access_token directly (matches what /api/auth/google expects)
    onSuccess: exchangeGoogleToken,
    onError: e => {
      console.error('[auth] Google popup error:', e)
      setError('Google sign-in was cancelled or failed.')
    },
    onNonOAuthError: e => {
      console.error('[auth] Google non-OAuth error:', e)
      setError('Google sign-in failed (popup blocked or closed).')
    },
  })

  const signInGoogle = useCallback((rememberMe = false): void => {
    rememberRef.current = rememberMe
    setError(null)
    googleLogin()
  }, [googleLogin])

  // STUB — wire up MSAL when client-facing sign-in is in scope. Note the email
  // magic link below already makes Microsoft (and every other host) usable
  // today without provider-specific OAuth.
  const signInMicrosoft = useCallback((_rememberMe = false): void => {
    console.warn('[auth] signInMicrosoft: not implemented — use the email sign-in link.')
    setError('Microsoft SSO isn\'t available yet — use "Email me a sign-in link" instead.')
  }, [])

  // Ask the server to email a one-time sign-in link. The `rememberMe` choice is
  // sent now and baked into the eventual session TTL server-side. Resolves true
  // on any well-formed request: the endpoint is enumeration-safe (always ok),
  // so the UI must show the same "check your inbox" state whether or not the
  // address has an account.
  const requestEmailLink = useCallback(async (email: string, rememberMe = false): Promise<boolean> => {
    setError(null)
    try {
      const res = await callApi(`/api/auth/email/request-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, rememberMe }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as WorkerAuthResponse
        throw new Error(data.error ?? `Couldn't send the link (${res.status})`)
      }
      return true
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      console.error('[auth] email link request failed:', msg)
      setError(msg)
      return false
    }
  }, [])

  // Trade the link's one-time token for a session JWT (same store path as Google).
  const verifyEmailLink = useCallback(async (linkToken: string): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await callApi(`/api/auth/email/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: linkToken }),
      })
      const data = await res.json() as WorkerAuthResponse
      if (!res.ok) throw new Error(data.error ?? `Sign-in failed (${res.status})`)
      // Local expiry comes from the JWT the server just issued — it already
      // reflects the remember-me choice, so we don't re-derive a TTL here.
      const expiresAt = jwtExpiryMs(data.token) ?? Date.now() + 8 * 3600 * 1000
      const newStored: StoredAuth = {
        user: {
          email: data.email,
          name: data.name,
          picture: data.picture,
          provider: 'email',
          role: data.role ?? 'viewer',  // least-privilege default (O9/D3)
          clientId: data.clientId ?? null,
        },
        token: data.token,
        expiresAt,
      }
      persist(newStored)
      setStored(newStored)
      trackUsage(API_BASE, { Authorization: `Bearer ${data.token}` }, 'login')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      console.error('[auth] email link sign-in failed:', msg)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  const signOut = useCallback((sessionExpired = false): void => {
    // If a "sign in as client" session is what just ended (expired lens token,
    // 401, or an explicit sign-out click), fall back to the staffer's own
    // session instead of dumping them on the sign-in card — exiting the client
    // is not the same as leaving the building. That session was never
    // overwritten, so it is simply read back.
    const hadLens = safeGet(sessionStorage, CLIENT_SESSION_KEY) !== null
    safeRemove(sessionStorage, CLIENT_SESSION_KEY)
    if (hadLens) {
      const own = readOwnSession()
      if (own.kind === 'ok') {
        setStored(own.stored)
        setError(null)
        return
      }
    }
    safeRemove(localStorage, STORAGE_KEY)
    safeRemove(localStorage, LEGACY_REAL_SESSION_KEY)
    clearViewAs()
    // Saved support drafts are customer text — names, plates, amounts. They
    // outlive the poll and a closed tab by design, but must not outlive the
    // session on a shared workstation.
    clearAllDrafts()
    setStored(null)
    setViewAs(null)
    setError(sessionExpired ? SESSION_EXPIRED_MESSAGE : null)
  }, [])

  // Latest signOut, read by the global fetch interceptor without re-installing it.
  const signOutRef = useRef(signOut)
  signOutRef.current = signOut

  // Global 401 handler — any API call that comes back 401 means the session
  // is dead server-side (expired, secret rotated, clock skew). Patch window.fetch
  // once so every screen's calls bounce to the login screen instead of surfacing
  // a cryptic "HTTP 401". Installed regardless of auth state so a 401 mid-session
  // is always caught; harmless when logged out (signOut is idempotent).
  useEffect(() => {
    const original = window.fetch
    const patched: typeof window.fetch = async (input, init) => {
      // View as renders the target's buttons (so the console looks exactly like
      // theirs), which means a lensed click CAN now reach a write. Refuse it
      // here, before the network, with the worker's own contract: the worker
      // still 403s it regardless (api/src/index.ts VIEW_AS_HEADER), this just
      // keeps a click from writing a viewas.blocked audit row per attempt.
      if (isViewAsBlockedRequest(input, init, API_BASE)) {
        return new Response(JSON.stringify({ error: VIEW_AS_BLOCKED_MESSAGE }), {
          status: 403, headers: { 'Content-Type': 'application/json' },
        })
      }
      const res = await original(input, init)
      if (res.status === 401) {
        const reqUrl = typeof input === 'string' ? input
          : input instanceof URL ? input.href
          : input instanceof Request ? input.url : ''
        // Only react to our own API — never sign out on a 401 from Google/3rd-party.
        // Don't bounce on the sign-in exchange itself (bad token there is a login error, not an expired session).
        if (reqUrl.startsWith(API_BASE) && !reqUrl.includes('/api/auth/')) {
          signOutRef.current(true)
        }
      }
      return res
    }
    window.fetch = patched
    return () => { window.fetch = original }
  }, [])

  const realUser = stored?.user ?? null

  // Only Parkify staff (clientId null) with view.parkify_admin — owner/admin —
  // may impersonate. Computed from the REAL user, never the target, so the
  // lens can always be exited even mid-impersonation.
  const canImpersonate = realUser != null
    && realUser.clientId === null
    && hasPermission(realUser.role, 'view.parkify_admin')

  // Ignore a stale persisted target if the real user isn't eligible.
  const activeViewAs = canImpersonate ? viewAs : null

  // authHeaders ALWAYS carries the real user's token — never the target's.
  // While a view-as lens is active we ALSO send X-Parkify-View-As: <target>,
  // which the worker uses to enforce the read-only contract server-side (it
  // 403s any mutating request under the lens — see parkify-pay/api index.ts).
  // The client perm gate is now just the first line of defense; this header is
  // what makes "read-only" a real guarantee rather than a UI convention.
  const authHeaders = useCallback((): HeadersInit => {
    if (!stored) return {}
    const headers: Record<string, string> = { Authorization: `Bearer ${stored.token}` }
    if (activeViewAs) headers['X-Parkify-View-As'] = activeViewAs.email
    return headers
  }, [stored, activeViewAs])

  const realAuthHeaders = useCallback((): HeadersInit => (
    stored ? { Authorization: `Bearer ${stored.token}` } : {}
  ), [stored])

  // Effective identity the rest of the app renders + gates against. While
  // viewing-as, adopt the target's role/permissions/clientId/tenant so every
  // nav item, screen, and button gate matches exactly what that user sees.
  // Permissions are derived locally from the role (same matrix the server
  // mirrors) — no extra fetch needed.
  const effectiveUser = useMemo<AuthUser | null>(() => {
    if (!realUser) return null
    if (!activeViewAs) return realUser
    return {
      ...realUser,
      email: activeViewAs.email,
      name: activeViewAs.name,
      picture: undefined,
      role: activeViewAs.role,
      clientId: activeViewAs.clientId,
      permissions: activeViewAs.permissions ?? getPermissionsForRole(activeViewAs.role),
      client: activeViewAs.client ?? null,
      // Never the operator's own scope under someone else's name. The lens has
      // no /me of its own here, and the Worker refuses location writes under
      // any lens anyway, so unknown (fail closed) is the honest value.
      scope: null,
    }
  }, [realUser, activeViewAs])

  // Drop a persisted target the moment the real user becomes ineligible
  // (role downgraded, or a client-scoped login somehow carried the key over).
  useEffect(() => {
    if (!canImpersonate && viewAs) {
      clearViewAs()
      setViewAs(null)
    }
  }, [canImpersonate, viewAs])

  const startViewingAs = useCallback((target: ViewAsTarget): void => {
    if (!canImpersonate) return
    saveViewAs(target)
    setViewAs(target)
  }, [canImpersonate])

  const stopViewingAs = useCallback((): void => {
    clearViewAs()
    setViewAs(null)
  }, [])

  // Re-read the lens target LIVE whenever a lens opens and on every reload.
  // The persisted target is a copy taken when the picker was clicked, while the
  // worker applies the target's CURRENT role/client/zones to every lensed
  // request. Moving someone to another client (or changing their role or hidden
  // screens) with a lens open used to leave the banner, nav and gates on the old
  // copy, so "View as" showed a console nobody has. Reads go out as the REAL
  // operator: the user list is staff-only and 403s through the lens.
  const viewAsEmail = activeViewAs?.email ?? null
  const realToken = stored?.token ?? null
  useEffect(() => {
    if (!viewAsEmail || !realToken) return
    let cancelled = false
    const headers = { Authorization: `Bearer ${realToken}` }
    void Promise.all([
      callApi(`/api/admin/users`, { headers })
        .then(r => (r.ok ? r.json() as Promise<unknown> : null)),
      callApi(`/api/clients`, { headers })
        .then(r => (r.ok ? r.json() as Promise<unknown> : null))
        .catch(() => null),
      // The worker checks the target's role against THIS matrix (customisable
      // in Parkify Admin → Roles), so the console's gates must too.
      callApi(`/api/admin/permissions`, { headers })
        .then(r => (r.ok ? r.json() as Promise<{ matrix?: PermissionMatrix }> : null))
        .catch(() => null),
    ]).then(([users, clients, perms]) => {
      // Unreadable user list: keep the copy we have. The worker still scopes
      // every read from the live record, so data never follows the stale copy.
      if (cancelled || !Array.isArray(users)) return
      const record = (users as Array<ViewAsUserRecord & { active?: boolean }>)
        .find(u => u.email?.toLowerCase() === viewAsEmail.toLowerCase())
      // Removed or deactivated since the lens opened: the worker now answers
      // 400 to every lensed read, so leave the lens rather than render errors.
      if (!record || record.active === false) {
        clearViewAs()
        setViewAs(null)
        return
      }
      const clientMap = new Map<string, ViewAsClientRecord>()
      for (const c of (Array.isArray(clients) ? clients as ViewAsClientRecord[] : [])) clientMap.set(c.id, c)
      setViewAs(prev => {
        if (!prev || prev.email.toLowerCase() !== viewAsEmail.toLowerCase()) return prev
        const matrix = perms?.matrix ?? null
        const fresh = buildViewAsTarget(record, clientMap, matrix)
        // Client list unreadable: keep the branding/flags we had, but only if
        // the target is still on the same client.
        if (!Array.isArray(clients) && prev.clientId === fresh.clientId) fresh.client = prev.client ?? null
        // Matrix unreadable: keep the permissions we had for the same role.
        if (!matrix && prev.role === fresh.role && prev.permissions) fresh.permissions = prev.permissions
        if (JSON.stringify(fresh) === JSON.stringify(prev)) return prev
        saveViewAs(fresh)
        return fresh
      })
    }).catch(() => { /* keep the copy; see above */ })
    return () => { cancelled = true }
  }, [viewAsEmail, realToken])

  // ── Sign in as a client — REAL token swap, not the view-as lens ──────────
  const clientSession = stored?.clientSession ?? null

  const signInAsClient = useCallback(async (client: { id: string; name: string }): Promise<boolean> => {
    // Server enforces manage.clients + staff-only; this is just the UI gate.
    // No nesting: a client session can't mint another one (its token would be
    // refused server-side anyway).
    if (!stored || stored.clientSession || !canImpersonate) return false
    setError(null)
    try {
      const res = await callApi(`/api/clients/${encodeURIComponent(client.id)}/sign-in-as`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${stored.token}` },
      })
      const data = await res.json().catch(() => ({})) as {
        token?: string; clientId?: string; clientName?: string; expiresAt?: number; error?: string
      }
      if (!res.ok || !data.token) throw new Error(data.error ?? `Sign-in as client failed (${res.status})`)
      // Make the client session THE session for this tab (persist() puts it in
      // sessionStorage; the staffer's own session stays where it is). View-as
      // is cleared — mixing the two lenses would be nonsense.
      clearViewAs()
      setViewAs(null)
      const lensStored: StoredAuth = {
        user: {
          email: stored.user.email,
          name: stored.user.name,
          picture: stored.user.picture,
          provider: stored.user.provider,
          role: 'manager',
          clientId: data.clientId ?? client.id,
        },
        token: data.token,
        expiresAt: data.expiresAt ?? (jwtExpiryMs(data.token) ?? Date.now() + 3600_000),
        clientSession: { clientId: data.clientId ?? client.id, clientName: data.clientName ?? client.name },
      }
      persist(lensStored)
      setStored(lensStored)
      return true
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      console.error('[auth] sign-in-as-client failed:', msg)
      setError(msg)
      return false
    }
  }, [stored, canImpersonate])

  const switchClient = useCallback(async (clientId: string): Promise<boolean> => {
    // Only a person's own session; a lens has no memberships of its own (the
    // server refuses it too).
    if (!stored || stored.clientSession || viewAs) return false
    setError(null)
    try {
      const res = await callApi(`/api/admin/users/me/switch-client`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${stored.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId }),
      })
      const data = await res.json().catch(() => ({})) as {
        token?: string; clientId?: string; role?: Role; expiresAt?: number; error?: string
      }
      if (!res.ok || !data.token) throw new Error(data.error ?? `Switching client failed (${res.status})`)
      const next: StoredAuth = {
        user: {
          email: stored.user.email,
          name: stored.user.name,
          picture: stored.user.picture,
          provider: stored.user.provider,
          role: data.role ?? 'viewer',  // least-privilege default (O9/D3)
          clientId: data.clientId ?? clientId,
        },
        token: data.token,
        expiresAt: data.expiresAt ?? (jwtExpiryMs(data.token) ?? stored.expiresAt),
      }
      persist(next)
      // Start the console over in the new client: screens hold zone lists,
      // filters and drafts from the client being left, and none may carry over.
      window.location.assign('/')
      return true
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      console.error('[auth] switch client failed:', msg)
      setError(msg)
      return false
    }
  }, [stored, viewAs])

  const exitClientSession = useCallback((): void => {
    if (!stored?.clientSession) return
    signOut() // restores the staffer's own session when it's still valid
  }, [stored, signOut])

  // Prefer the live server-issued permissions list. Falls back to the local
  // default matrix if /me hasn't returned yet (cold-start before refresh).
  const hasPerm = useCallback((perm: Permission): boolean => {
    // Viewing-as answers with the TARGET's permissions, so every button they
    // have renders (the lens must look exactly like their console). Clicking
    // one is still refused: the fetch interceptor above and the worker's
    // VIEW_AS_HEADER block both deny every lensed write, whatever a screen
    // renders. Two deliberate carve-outs ACT: editing the impersonated
    // client's ZONES or RATES, so a sales/support call can set up a client
    // live. Those answer with the REAL operator's own permission, never the
    // target's, mirroring the server-side gate (api/src/index.ts: the block
    // there runs before role gets swapped).
    if (activeViewAs && (perm === 'edit.zones' || perm === 'edit.rates')) {
      const realPerms = realUser?.permissions
      return realPerms ? realPerms.includes(perm) : hasPermission(realUser?.role, perm)
    }
    const perms = effectiveUser?.permissions
    if (perms) return perms.includes(perm)
    return hasPermission(effectiveUser?.role, perm)
  }, [effectiveUser, activeViewAs, realUser])

  // Feature flag check. Falls back to the registry default when:
  //  - /me hasn't returned yet (cold start)
  //  - the client record has no `features` map
  //  - the specific flag isn't set in the map
  // Explicitly `false` is the only thing that hides a default-on feature. While
  // viewing-as this reads the target tenant's flags, so feature-gated nav
  // matches theirs. The absent-means-default rule itself lives in
  // isFeatureEnabled, so this and the worker can't drift on it.
  const hasFeature = useCallback((key: keyof ClientFeatures): boolean => {
    // Per-user narrowing follows the EFFECTIVE user. While viewing-as, the point
    // is to see what the TARGET sees: their own hidden screens apply, and the
    // staff viewer's personal ones must not (that would show a sidebar neither
    // of them has, and read as "this client is missing a screen" on a call).
    const userFeatures = activeViewAs ? (activeViewAs.userFeatures ?? null) : realUser?.userFeatures
    return isFeatureEnabledFor(effectiveUser?.client?.features, userFeatures, key)
  }, [effectiveUser, activeViewAs, realUser])

  // Apply tenant branding to the document — sets a CSS variable that
  // brand-aware components (sidebar, buttons) can read. Defaults to the
  // Parkify purple when the tenant didn't override. Follows the effective
  // user so viewing-as re-themes to the target tenant's accent.
  useEffect(() => {
    const accent = effectiveUser?.client?.accentColor || '#7F3F98'
    document.documentElement.style.setProperty('--parkify-accent', accent)
  }, [effectiveUser?.client?.accentColor])

  // Refresh role from server on mount/refocus — picks up role changes made
  // by an admin without forcing the user to re-login.
  useEffect(() => {
    if (!stored) return
    let cancelled = false
    const refresh = async () => {
      try {
        const res = await callApi(`/api/admin/users/me`, {
          headers: { Authorization: `Bearer ${stored.token}` },
        })
        if (!res.ok || cancelled) return
        const data = await res.json() as {
          role?: Role
          name?: string
          permissions?: Permission[]
          clientId?: string | null
          client?: ClientContext | null
          userFeatures?: UserFeatures | null
          scope?: unknown
          memberships?: ClientMembershipOption[]
          portfolio?: boolean
        }
        if (!data.role) return
        // Always update state when we got a successful /me response — branding
        // and feature flags can change without role/permission changes, and
        // we want those to land immediately.
        const updated = {
          ...stored,
          user: {
            ...stored.user,
            role: data.role,
            name: data.name ?? stored.user.name,
            clientId: data.clientId ?? null,
            permissions: data.permissions,
            client: data.client ?? null,
            userFeatures: data.userFeatures ?? null,
            // Stored as received, and REPLACED on every /me (absent becomes
            // undefined), so a stale persisted scope never outlives a response
            // that omits one. Readers validate it (parseCallerScope) and fail closed.
            scope: data.scope,
            memberships: Array.isArray(data.memberships) ? data.memberships : [],
            portfolio: data.portfolio === true,
          },
        }
        persist(updated); setStored(updated)
      } catch { /* network blip */ }
    }
    void refresh()
    const onFocus = () => void refresh()
    window.addEventListener('focus', onFocus)
    return () => { cancelled = true; window.removeEventListener('focus', onFocus) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored?.token])

  // Auto-clear when the stored token expires.
  //
  // Both paths pass sessionExpired=true so the sign-in screen SAYS "Your
  // session expired" — this is the lapse-while-the-tab-sits-open case, and a
  // bare signOut() sets error to null, which dropped the operator onto a
  // wordless sign-in card with no idea why they'd been kicked out. The 401
  // interceptor above already passed true; this proactive path didn't.
  // NB: setTimeout(signOut, ms) would invoke it with no args (defaulting
  // sessionExpired to false), hence the wrapping arrow.
  useEffect(() => {
    if (!stored) return
    const ms = stored.expiresAt - Date.now()
    if (ms <= 0) { signOut(true); return }
    const t = setTimeout(() => signOut(true), ms)
    return () => clearTimeout(t)
  }, [stored, signOut])

  const value = useMemo<AuthContextValue>(() => ({
    user: effectiveUser,
    loading,
    error,
    signInGoogle,
    signInMicrosoft,
    googleConfigured: GOOGLE_CONFIGURED,
    requestEmailLink,
    verifyEmailLink,
    signOut,
    authHeaders,
    realAuthHeaders,
    hasPerm,
    hasFeature,
    realUser,
    viewingAs: activeViewAs,
    canImpersonate,
    startViewingAs,
    stopViewingAs,
    clientSession,
    signInAsClient,
    exitClientSession,
    switchClient,
  }), [effectiveUser, loading, error, signInGoogle, signInMicrosoft, requestEmailLink, verifyEmailLink, signOut, authHeaders, realAuthHeaders, hasPerm, hasFeature, realUser, activeViewAs, canImpersonate, startViewingAs, stopViewingAs, clientSession, signInAsClient, exitClientSession, switchClient])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>')
  return ctx
}
